import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import AppImage from '../AppImage';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DeleteAccountModal from './DeleteAccountModal';
import ProfileSettingsModal from './ProfileSettingsModal';

const Header = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false);
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsUserMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: '/dashboard-overview', 
      icon: 'BarChart3',
      description: 'View training analytics and wellness trends'
    },
    { 
      label: 'Wellness', 
      path: '/daily-wellness', 
      icon: 'Heart',
      description: 'Track daily wellness metrics'
    },
    { 
      label: 'Maturation', 
      path: '/maturation-monitor', 
      icon: 'TrendingUp',
      description: 'Monitor growth and development'
    },
    { 
      label: 'Guide', 
      path: '/how-it-works', 
      icon: 'BookOpen',
      description: 'Learn about load recovery methodology'
    }
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleOpenProfileSettings = () => {
    setIsUserMenuOpen(false);
    setIsProfileSettingsOpen(true);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-surface border-b border-border">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo */}
        <Link to="/dashboard-overview" className="flex items-center space-x-3">
          <div className="flex items-center justify-center">
            <AppImage
              src="/assets/images/Hub_LogoHI-1762941543093.png"
              alt="Sports Science Platform Logo - The Hub with torch icon and colorful dynamic swoosh elements"
              className="h-10 w-auto"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-semibold text-text-primary leading-tight">
              Sports Science Platform
            </span>
            <span className="text-xs text-text-secondary leading-tight">
              Performance Analytics
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ease-out hover:bg-muted ${
                isActivePath(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              title={item?.description}
            >
              <Icon 
                name={item?.icon} 
                size={16} 
                color="currentColor" 
                strokeWidth={2}
              />
              <span>{item?.label}</span>
            </Link>
          ))}
        </nav>

        {/* User Menu */}
        <div className="hidden md:flex items-center space-x-3 relative">
          <button
            onClick={toggleUserMenu}
            className="flex items-center space-x-2 px-3 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
            aria-label="User menu"
          >
            <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center">
              <Icon name="User" size={14} color="white" strokeWidth={2} />
            </div>
            <span className="text-sm font-medium text-text-primary">
              {userProfile?.fullName || user?.email?.split('@')?.[0] || 'Athlete'}
            </span>
            <Icon 
              name={isUserMenuOpen ? "ChevronUp" : "ChevronDown"} 
              size={16} 
              color="currentColor" 
            />
          </button>

          {/* User Dropdown Menu */}
          {isUserMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-64 bg-surface border border-border rounded-lg shadow-lg py-2 z-50">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-border">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                    <Icon name="User" size={18} color="white" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {userProfile?.fullName || 'Current User'}
                    </p>
                    <p className="text-xs text-text-secondary truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-2">
                <button
                  onClick={handleOpenProfileSettings}
                  className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-muted transition-colors"
                >
                  <Icon name="Settings" size={16} color="currentColor" />
                  <span>Profile Settings</span>
                </button>

                <div className="border-t border-border my-2"></div>
                
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-muted transition-colors"
                >
                  <Icon name="LogOut" size={16} color="currentColor" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleMenu}
          className="md:hidden"
          aria-label="Toggle navigation menu"
        >
          <Icon 
            name={isMenuOpen ? "X" : "Menu"} 
            size={20} 
            color="currentColor" 
          />
        </Button>
      </div>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-surface border-t border-border">
          <nav className="px-6 py-4 space-y-2">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setIsMenuOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-150 ease-out ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                }`}
              >
                <Icon 
                  name={item?.icon} 
                  size={18} 
                  color="currentColor" 
                  strokeWidth={2}
                />
                <span>{item?.label}</span>
              </Link>
            ))}

            {/* Mobile User Actions */}
            <div className="pt-4 mt-4 border-t border-border space-y-2">
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsProfileSettingsOpen(true);
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-muted transition-colors"
              >
                <Icon name="Settings" size={18} color="currentColor" />
                <span>Profile Settings</span>
              </button>

              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  handleSignOut();
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-muted transition-colors"
              >
                <Icon name="LogOut" size={18} color="currentColor" />
                <span>Sign Out</span>
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Modals */}
      <ProfileSettingsModal
        isOpen={isProfileSettingsOpen}
        onClose={() => setIsProfileSettingsOpen(false)}
        onOpenDeleteModal={handleOpenDeleteModal}
      />
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        userEmail={user?.email}
      />
    </header>
  );
};

export default Header;